import { Either, Maybe } from 'monet';
import LeftResponse from '@/storage/LeftResponse';

/**
 * @description Запрос на внесение\изъятие наличных.
 * @param {string}  cashboxId             Идентификатор кассы.
 * @param {string}  command               Команда.
 * @param {number}  amount                Сумма.
 * @param {string}  note                  Примечание.
 * @param {?string} withdrawalPurposeId   Идентификатор цели изъятия.
 * @param {?string} withdrawalPurposeName Название цели изъятия.
 * @param {?string} withdrawalEmployeeId  Идентификатор сотрудника изъятия.
 * @constructor
 */
export function CashInOutRequest(
  cashboxId,
  command,
  amount,
  note = '',
  withdrawalPurposeId = null,
  withdrawalPurposeName = '',
  withdrawalEmployeeId = null,
) {
  this.getCashboxId = () => cashboxId;
  this.getCommand = () => command;
  this.toQuery = () => {
    const query = { amount, note };
    if (withdrawalPurposeId !== null) {
      query.withdrawal_purpose_id = withdrawalPurposeId;
      if (withdrawalPurposeName) {
        query.withdrawal_purpose_name = withdrawalPurposeName;
      }
    }
    if (withdrawalEmployeeId !== null) {
      query.withdrawal_employee_id = withdrawalEmployeeId;
    }
    return query;
  };
}

/**
 * @description Шлюз кассы.
 * @param {Axios} axios HTTP клиент.
 * @constructor
 */
export default function CashboxGateway(axios) {
  /**
   * @description Получить ссылку на скачивание приложения.
   * @returns {Promise<Either>}
   */
  this.downloadOnlineCheckApp = () => axios
    .get('/api/online-check/download-app')
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Принудительно закрыть смену.
   * @param {number} commandId Идентификатор команды.
   * @returns {Promise<Either>}
   */
  this.forceCloseShift = (commandId) => axios
    .post(`/api/online-check/commands/${commandId}/force-close`)
    .then(
      async (response) => Either.right(response.data.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Открыть смену.
   * @returns {Promise<Either>}
   */
  this.openShift = () => axios.post('/api/cashbox/shifts').then(
    async (response) => Either.right(response.data.data),
    async (error) => {
      if (error.isAxiosError && [400, 403].includes(error.response.status)) {
        const { causes, title } = error.response.data;
        const leftResponse = new LeftResponse(causes, title);
        return Either.left(leftResponse);
      }
      throw error;
    },
  );
  /**
   * @description Закрыть смену.
   * @param {number} shiftId Идентификатор смены.
   * @returns {Promise<Either>}
   */
  this.closeShift = (shiftId) => axios
    .post(`/api/cashbox/shifts/${shiftId}/close`)
    .then(
      async (response) => Either.right(response.data.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Закрыть внешнюю смену.
   * @returns {Promise<Either>}
   */
  this.closeExternalShift = () => axios
    .post('/api/online-check/close-external-shift')
    .then(
      async (response) => Either.right(response.data.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Просмотр текущей смены.
   * @returns {Promise<Either>}
   */
  this.viewShift = () => axios
    .get('/api/cashbox/shifts/latest')
    .then(
      async (response) => Either.right(response.data.data),
      async (error) => {
        if (error.isAxiosError) {
          if (error.response && [400, 403].includes(error.response.status)) {
            const { causes, title } = error.response.data;
            const leftResponse = new LeftResponse(causes, title);
            return Either.left(leftResponse);
          }
          if (!error.response) {
            throw new Error('Network error: сервер по адресу /api/cashbox/shifts/latest не ответил на запрос');
          }
        }
        throw error;
      },
    );

  /**
   * @description Получить результат команды.
   * @param {number} commandId Идентификатор команды.
   * @returns {Promise<Either>}
   */
  this.getCommandResult = (commandId) => axios
    .get(`/api/cashbox/commands/${commandId}`)
    .then(
      async (response) => Either.right(response.data.data),
      async (error) => {
        if (error.isAxiosError) {
          if (error.response && [400, 403, 404].includes(error.response.status)) {
            const { causes, title } = error.response.data;
            const leftResponse = new LeftResponse(causes, title);
            return Either.left(leftResponse);
          }
          if (!error.response) {
            throw new Error(`Network error: сервер по адресу /api/cashbox/commands/${commandId} не ответил на запрос`);
          }
        }
        throw error;
      },
    );

  /**
   * @description Отправить запрос на печать отчета.
   * @param {string} reportType Тип отчета.
   * @returns {Promise<Either>}
   */
  this.printReport = (reportType) => axios
    .post('/api/cashbox/report', { report_type: reportType })
    .then(
      async (response) => Either.right(response.data.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Отправить запрос на внесение\изъятие наличных.
   * @param {CashInOutRequest} request Запрос.
   * @returns {Promise<Either>}
   */
  this.cashInOut = (request) => axios
    .post(
      `/api/cashboxes/${request.getCashboxId()}/cash/${request.getCommand()}`,
      request.toQuery(),
    )
    .then(
      async (response) => Either.right(response.data.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Показать опции для операций со счетами.
   * @returns {Promise<Maybe>}
   */
  this.viewInvoiceOperationOptions = () => axios
    .get('/api/invoice-operation-options')
    .then(({ data }) => {
      const periodTypes = data.period_types.map(({
        from_to: fromTo,
        title,
        value,
      }) => ({
        fromTo,
        title,
        value,
      }));
      return Maybe.of({ periodTypes, employees: data.employees });
    });

  /**
   * @description Показать список касс.
   * @returns {Promise<Either>}
   */
  this.viewList = () => axios
    .get('/api/cashboxes')
    .then(
      (response) => Either.right(response.data.data),
      (error) => {
        if (error.isAxiosError) {
          if (error.response && error.response.status === 403) {
            return Either.Left(error.response.data.title);
          }
          if (!error.response) {
            throw new Error('Network error: сервер по адресу /api/cashboxes не ответил на запрос');
          }
        }
        throw error;
      },
    );

  /**
   * @description Показать список касс для настроек.
   * @returns {Promise<Either>}
   */
  this.viewListForSettings = () => axios
    .get('/api/cashboxes-for-settings')
    .then(
      (response) => Either.of(response.data.data),
      (error) => {
        if (error.isAxiosError && error.response.status === 403) {
          return Either.Left(error.response.data.title);
        }
        throw error;
      },
    );

  this.editCashboxOperationNote = (operationId, note) => axios
    .put(`/api/cashbox-operations/${operationId}/note`, { note })
    .then(
      (response) => Either.right(response.data.message),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.left(error.response.data.title);
        }
        throw error;
      },
    );

  this.editCashboxOperationEmployee = (operationId, employee) => axios
    .put(`/api/cashbox-operations/${operationId}/employee`, { employee })
    .then(
      (response) => Either.right(response.data.message),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.left(error.response.data.title);
        }
        throw error;
      },
    );

  /**
   * @description Создать кассу.
   * @param {any} request Запрос.
   * @returns {Promise<Either>}
   */
  this.createCashbox = (request) => axios
    .post('/api/cashboxes', request)
    .then(
      (response) => Either.of(response.data.message),
      (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Удалить кассу.
   * @param {number} id Идентификатор кассы.
   * @returns {Promise<Either>}
   */
  this.deleteCashbox = (id) => axios
    .delete(`/api/cashboxes/${id}`)
    .then(
      (response) => Either.of(response.data.message),
      (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Изменить кассу.
   * @param {number} id      Идентификатор кассы.
   * @param {any}    request Запрос.
   * @returns {Promise<Either>}
   */
  this.editCashbox = (id, request) => axios
    .put(`/api/cashboxes/${id}`, request)
    .then(
      (response) => Either.of(response.data.message),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Установить кассу по умолчанию.
   * @param {number} id Идентификатор кассы.
   * @returns {Promise<Either>}
   */
  this.setDefaultCashbox = (id) => axios
    .put(`/api/cashboxes/${id}/default`)
    .then(
      (response) => Either.of(response.data.message),
      (error) => {
        if (error.isAxiosError) {
          if (error.response && [400, 403, 404].includes(error.response.status)) {
            return Either.left(error.response.data.title);
          }
          if (!error.response) {
            throw new Error(`Network error: сервер по адресу /api/cashboxes/${id}/default не ответил на запрос`);
          }
        }
        throw error;
      },
    );

  /**
   * @description Просмотр баланса кассы.
   * @param {number} cashboxId
   * @returns {Promise<Either>}
   */
  this.viewCashboxBalance = (cashboxId) => axios
    .get(`/api/cashboxes/${cashboxId}/balance`)
    .then(
      (response) => Either.of({
        cashBalance: response.data.cash_balance,
        cashlessBalance: response.data.cashless_balance,
      }),
      (error) => {
        if (
          error.isAxiosError && (
            error.response.status === 400
            || error.response.status === 403
            || error.response.status === 404
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Показать операции всех счетов пациентов за период по кассе.
   * @param {number} cashboxId Идентификатор кассы.
   * @param {string} dateFrom  Дата начала.
   * @param {string} dateTo    Дата окончания.
   * @returns {Promise<Either>}
   */
  this.viewOperationsAllPatientInvoicesForPeriod = (cashboxId, dateFrom, dateTo) => axios
    .get(`/api/cashboxes/${cashboxId}/operations-all-patient-invoices/${dateFrom}:${dateTo}`)
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (
          error.isAxiosError && (
            error.response.status === 400
            || error.response.status === 403
            || error.response.status === 404
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Показать операции всех счетов пациентов за период в виде таблицы.
   * @param {number} cashboxId Идентификатор кассы.
   * @param {string} dateFrom  Дата начала.
   * @param {string} dateTo    Дата окончания.
   * @returns {Promise<Either>}
   */
  this.viewOperationsAllPatientInvoicesForPeriodBySheet = (cashboxId, dateFrom, dateTo) => axios
    .get(
      `/api/cashboxes/${cashboxId}/operations-all-patient-invoices/${dateFrom}:${dateTo}/spreadsheet`,
      { responseType: 'blob' },
    )
    .then(
      (response) => Either.Right(response.data),
      async (error) => {
        if (error.isAxiosError && error.response.status === 400) {
          const { data: blob } = error.response;
          if (blob.type === 'application/json') {
            const data = JSON.parse(await blob.text());
            return Either.Left(data);
          }
        }
        throw error;
      },
    );

  /**
   * @description Получить состояние смены.
   * @returns {Promise<Either>}
   */
  this.viewShiftState = () => axios
    .get('/api/cashbox/shift-state')
    .then(
      async (response) => Either.right(response.data.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Получить список целей изъятия из кассы.
   * @returns {Promise<Maybe>}
   */
  this.viewWithdrawalPurposes = () => axios
    .get('/api/cashbox-withdrawal-purposes')
    .then(
      (response) => Maybe.some(response.data.data),
      (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          return Maybe.none();
        }
        throw error;
      },
    );

  /**
   * @description Удалить цель изъятия из кассы.
   * @returns {Promise<Maybe>}
   */
  this.removeWithdrawalPurpose = (purposeId) => axios
    .delete(`/api/cashbox-withdrawal-purposes/${purposeId}`)
    .then(
      (response) => Either.right(response.data.message),
      (error) => {
        if (error.isAxiosError && [403, 404].includes(error.response.status)) {
          return Either.left(error.response.data.title);
        }
        throw error;
      },
    );

  /**
   * @description Изменить операцию.
   * @param {number} operationId
   * @param {string} paymentMode
   * @param {number} amount
   * @returns {Promise<Either>}
   */
  this.changeOperation = (operationId, paymentMode, amount) => axios
    .put(`/api/cashbox-amount-operations/${operationId}`, { payment_mode: paymentMode, amount })
    .then(
      (response) => Either.right(response.data),
      (error) => {
        if (
          error.isAxiosError
          && error.response
          && [400, 403, 404].includes(error.response.status)
        ) {
          return Either.left(error.response.data);
        }
        throw error;
      },
    );
}
